import Modal from "common/Modal/Modal";
import { useEffect, useMemo, useState } from "react";
import LicensePackageInfo from "features/admin/group/users/LicensePackageInfo";
import Text from "common/Typography/Text";
import { displayResultMessage, openMessage } from "utils/misc";
import {
  AssignLicensesFailureType,
  FormattedLicenseAvailablePackage,
  LanguageNameSpaces,
  SubscriptionDuration,
  SubscriptionPlanDisplay,
  UserToPackage,
} from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface LicensesModalProps {
  closeModal: () => void;
  open: boolean;
  subGroupID: number;
  selectedTelegramIds: number[];
  setTelegramIdToError: (
    telegramIdToError?: Record<number, AssignLicensesFailureType>
  ) => void;
}

const LicensesModal = ({
  open,
  closeModal,
  subGroupID,
  selectedTelegramIds,
  setTelegramIdToError,
}: LicensesModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ADMIN);
  const [selectedPackage, setSelectedPackage] = useState<
    FormattedLicenseAvailablePackage | undefined
  >();
  const { data: licenseAvailablePackages } =
    sharedLib.sharedApi.admin.useGetLicenseAvailablePackagesQuery(
      { sub_group_id: subGroupID },
      { refetchOnMountOrArgChange: true, skip: !open }
    );
  const [
    assignLicenses,
    { data: assignLicensesResult, ...assignLicensesStatus },
  ] = sharedLib.sharedApi.admin.useAssignLicensesMutation();

  useEffect(() => {
    setSelectedPackage(undefined);
  }, [licenseAvailablePackages]);

  const getTelegramIdToError = () =>
    Object.entries(assignLicensesResult || {}).reduce(
      (acc, [failureType, telegramIds]) => {
        telegramIds.forEach((telegramId: number) => {
          if (failureType !== "assigned") {
            acc[telegramId] = failureType as AssignLicensesFailureType;
          }
        });
        return acc;
      },
      {} as Record<number, AssignLicensesFailureType>
    );

  const updateTelegramIdToError = () => {
    const telegramIdToError = getTelegramIdToError();
    setTelegramIdToError(
      Object.keys(telegramIdToError).length > 0 ? telegramIdToError : undefined
    );
  };

  useEffect(() => {
    if (!assignLicensesStatus.isLoading && assignLicensesResult) {
      const numRequestTelegramIds = Object.keys(
        assignLicensesStatus?.originalArgs?.user_to_package || {}
      ).length;
      const numAssigned = assignLicensesResult.assigned?.length || 0;
      const numFailed = numRequestTelegramIds - numAssigned;
      if (numFailed === 0) {
        openMessage(
          t("licenses.modal.successMessage", { count: numRequestTelegramIds })
        );
      } else {
        openMessage(
          t("licenses.modal.partialFailureMessage", {
            failed: numFailed,
            total: numRequestTelegramIds,
          }),
          "warning"
        );
      }
      updateTelegramIdToError();
      closeModal();
    }
  }, [assignLicensesStatus.isLoading]);

  const getAssignLicensesRequest = (
    selectedPackage: FormattedLicenseAvailablePackage
  ) => {
    const userToPackage = selectedTelegramIds.reduce((acc, telegramId) => {
      acc[telegramId] = [
        selectedPackage.subscriptionType,
        selectedPackage.subscriptionDuration,
      ];
      return acc;
    }, {} as UserToPackage);

    return {
      sub_group_id: subGroupID,
      user_to_package: userToPackage,
    };
  };

  const onAssignLicenses = () => {
    if (
      selectedPackage &&
      selectedTelegramIds.length <= selectedPackage.numLicenses
    ) {
      displayResultMessage(
        assignLicenses(getAssignLicensesRequest(selectedPackage)).unwrap()
      );
    } else {
      openMessage(t("licenses.modal.notEnoughError"), "warning");
    }
  };

  const formattedPackagesInfo = useMemo(
    () =>
      Object.entries(licenseAvailablePackages || {})
        .reduce((acc, [subscriptionType, durationToNumLicenses]) => {
          Object.entries(durationToNumLicenses).forEach(
            ([subscriptionDuration, numLicenses]) => {
              acc.push({
                subscriptionType: subscriptionType as SubscriptionPlanDisplay,
                subscriptionDuration: parseInt(
                  subscriptionDuration
                ) as SubscriptionDuration,
                numLicenses,
              });
            }
          );
          return acc;
        }, [] as Omit<FormattedLicenseAvailablePackage, "key">[])
        .map((packageInfo, index) => ({
          ...packageInfo,
          key: index,
        })) as FormattedLicenseAvailablePackage[],
    [licenseAvailablePackages]
  );

  return (
    <Modal
      title={t("licenses.modal.title")}
      open={open}
      onOk={onAssignLicenses}
      onCancel={closeModal}
      okText={t("licenses.modal.save")}
      okButtonProps={{
        disabled: !selectedPackage,
        loading: assignLicensesStatus.isLoading,
      }}
    >
      <Text style={{ marginBottom: 12 }}>
        {t("licenses.modal.description", { count: selectedTelegramIds.length })}
      </Text>
      {formattedPackagesInfo.map((packageInfo, index) => (
        <LicensePackageInfo
          key={index}
          packageInfo={packageInfo}
          setSelectedPackage={setSelectedPackage}
          selectedPackage={selectedPackage}
        />
      ))}
    </Modal>
  );
};

export default LicensesModal;
